import { useRef, useState, type ReactNode } from "react";
import { client, unwrap } from "@/app/lib/client";
import { useToast } from "@/app/components/system/Toast";
import { CameraIcon, SparkleIcon } from "@/app/components/system/Icons";

interface PlantPhotoPickerProps {
  photoImageUrl: string | null;
  fallback: ReactNode;
  size: number;
  onChange: (next: { id: string; url: string } | null) => void;
}

export function PlantPhotoPicker({ photoImageUrl, fallback, size, onChange }: PlantPhotoPickerProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  const openPicker = () => {
    if (uploading || !fileRef.current) return;
    fileRef.current.value = "";
    fileRef.current.click();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await unwrap(client.api.photos.$post({ form: { image: file } }));
      onChange(uploaded);
    } catch (err) {
      console.error("photo upload failed", err);
      toast.error("Couldn't upload that photo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={openPicker}
        disabled={uploading}
        aria-label={photoImageUrl ? "Replace photo" : "Add photo"}
        className="relative grid place-items-center rounded-full border-0 bg-transparent p-0 cursor-pointer transition-transform duration-150 hover:-translate-y-px disabled:cursor-wait disabled:opacity-80"
        style={{ width: size, height: size }}
      >
        <span
          className="grid place-items-center rounded-full overflow-hidden"
          style={{ width: size, height: size, boxShadow: "0 8px 24px rgba(40,30,10,.15)" }}
        >
          {photoImageUrl ? (
            <img
              src={photoImageUrl}
              alt=""
              className="block object-cover"
              style={{ width: size, height: size }}
            />
          ) : (
            fallback
          )}
        </span>
        <span
          className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-paper text-moss border border-line grid place-items-center"
          aria-hidden="true"
        >
          <CameraIcon size={16} stroke={1.8} />
        </span>
        {uploading && (
          <span
            className="absolute inset-0 grid place-items-center rounded-full"
            style={{ background: "oklch(0.99 0.006 90 / 0.65)", animation: "scan-overlay-pulse 1.4s ease-in-out infinite" }}
          >
            <SparkleIcon size={22} />
          </span>
        )}
      </button>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={openPicker}
          disabled={uploading}
          className="font-sans text-[13px] font-medium text-moss bg-transparent border-0 cursor-pointer tracking-[0.01em] disabled:text-ink-3 disabled:cursor-wait hover:text-ink"
        >
          {uploading ? "Uploading…" : photoImageUrl ? "Replace photo" : "Add a photo"}
        </button>
        {photoImageUrl && !uploading && (
          <>
            <span className="text-ink-3" aria-hidden="true">·</span>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="font-sans text-[13px] font-medium text-ink-3 bg-transparent border-0 cursor-pointer tracking-[0.01em] hover:text-ink"
            >
              Remove
            </button>
          </>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
    </div>
  );
}
